import { getOutlineControlProps } from 'utils/buttonUtils';
import { WhenLogic } from '@billing/validation/rules';
import {
  Button,
  Flex,
  Group,
  Paper,
  SegmentedControl,
  Stack,
  Text,
  useComputedColorScheme,
} from '@mantine/core';
import { IconPlus, IconSquarePlus, IconTrash } from '@tabler/icons-react';
import type { ReactElement } from 'react';
import { RuleCriteriaRow } from './RuleCriteriaRow';
import type { RuleBuilder } from './useRuleBuilder';

interface RuleCriteriaFieldsProps {
  builder: RuleBuilder;
}

export const RuleCriteriaFields = ({
  builder,
}: RuleCriteriaFieldsProps): ReactElement => {
  const colorScheme = useComputedColorScheme();
  const { translate } = builder;
  const requirementNumbers = builder.criteriaGroups.map(
    (_, index) => index + 1
  );
  const requirements =
    requirementNumbers.length > 1
      ? `${requirementNumbers.slice(0, -1).join(', ')} ${translate(
          'pages:rules.groups.listAnd'
        )} ${requirementNumbers.at(-1)}`
      : String(requirementNumbers[0]);
  const options = [
    {
      value: WhenLogic.All,
      label: translate('pages:rules.groups.allOptions'),
    },
    {
      value: WhenLogic.Any,
      label: translate('pages:rules.groups.anyOptions'),
    },
  ];
  return (
    <Stack gap="lg">
      <Stack gap="xs">
        <Text fw={700} size="lg">
          {translate(`pages:rules.groups.title.${builder.selectedAction}`)}
        </Text>
        <Text size="sm" c="dimmed">
          {translate(
            builder.whenLogic === WhenLogic.All
              ? 'pages:rules.groups.allRequirementsHint'
              : 'pages:rules.groups.anyRequirementHint'
          )}
        </Text>
        <SegmentedControl
          value={builder.whenLogic}
          onChange={(value) => {
            if (value === WhenLogic.All || value === WhenLogic.Any)
              builder.setWhenLogic(value);
          }}
          radius="xl"
          data={[
            {
              value: WhenLogic.All,
              label: translate('pages:rules.groups.and'),
            },
            { value: WhenLogic.Any, label: translate('pages:rules.groups.or') },
          ]}
          aria-label={translate('pages:rules.groups.betweenRequirements')}
          data-testid="rule-when-logic-select"
        />
      </Stack>
      {builder.criteriaGroups.map((group, groupIndex) => (
        <Paper
          key={group.id}
          withBorder
          radius="md"
          p="md"
          data-testid={`rule-group-${group.id}`}
        >
          <Stack gap="sm">
            <Group justify="space-between" align="flex-start">
              <Stack gap={4}>
                <Text fw={600}>
                  {translate('pages:rules.groups.requirement', {
                    number: groupIndex + 1,
                  })}
                </Text>
                <Text size="sm" c="dimmed">
                  {translate(
                    group.logic === WhenLogic.All
                      ? 'pages:rules.groups.allOptionsHint'
                      : 'pages:rules.groups.anyOptionHint'
                  )}
                </Text>
                <SegmentedControl
                  value={group.logic}
                  onChange={(value) => {
                    if (value === WhenLogic.All || value === WhenLogic.Any)
                      builder.setWhenGroupLogic(group.id, value);
                  }}
                  data={options}
                  aria-label={`${translate('pages:rules.groups.requirement', {
                    number: groupIndex + 1,
                  })}: ${translate('pages:rules.groups.withinRequirement')}`}
                  data-testid={`rule-group-logic-${group.id}`}
                  radius="xl"
                />
              </Stack>
              <Button
                {...getOutlineControlProps(
                  colorScheme,
                  builder.whenGroups.length === 1
                )}
                color={colorScheme === 'dark' ? 'white' : 'dark'}
                size="xs"
                leftSection={<IconTrash size={14} />}
                onClick={() => builder.removeWhenGroup(group.id)}
                disabled={builder.whenGroups.length === 1}
                data-testid={`rule-remove-group-${group.id}`}
              >
                {translate('common:buttons.remove')}
              </Button>
            </Group>
            {group.rows.map(({ clause, index, joinerKey }) => (
              <RuleCriteriaRow
                key={clause.id}
                builder={{
                  ...builder,
                  updateWhenClause: (rowID, update) =>
                    builder.updateWhenClause(rowID, update, group.id),
                  removeWhenClause: (rowID) =>
                    builder.removeWhenClause(rowID, group.id),
                }}
                clause={clause}
                index={index}
                joinerKey={joinerKey}
                canRemove={group.rows.length > 1}
              />
            ))}
            <Button
              variant="subtle"
              color="blue"
              leftSection={<IconPlus size={14} />}
              onClick={() => builder.addWhenClause(group.id)}
              data-testid={
                groupIndex === 0
                  ? 'rule-add-when-button'
                  : `rule-add-when-button-${group.id}`
              }
              w="fit-content"
              mr="auto"
            >
              {translate('pages:rules.groups.addOption')}
            </Button>
          </Stack>
        </Paper>
      ))}
      <Button
        size="lg"
        variant="subtle"
        c="white"
        color={colorScheme === 'dark' ? 'white' : 'dark'}
        bd={`1px dotted ${colorScheme === 'dark' ? 'white' : 'dark'}`}
        onClick={builder.addWhenGroup}
        fullWidth
        h="auto"
        py="sm"
        styles={{ label: { whiteSpace: 'normal' } }}
        data-testid="rule-add-group-button"
      >
        <Stack component="span" gap={4}>
          <Flex justify="center" align="center" gap={'sm'} c="teal">
            <IconSquarePlus size={14} />
            <Text component="span" inherit>
              {translate('pages:rules.groups.addRequirement')}
            </Text>
          </Flex>
          <Text
            component="span"
            size="xs"
            fw={400}
            c={colorScheme === 'dark' ? 'white' : 'dark'}
          >
            {translate(
              builder.whenLogic === WhenLogic.All
                ? 'pages:rules.groups.addRequirementAll'
                : 'pages:rules.groups.addRequirementAny',
              { count: requirementNumbers.length, requirements }
            )}
          </Text>
        </Stack>
      </Button>
      <Text size="xs" c="dimmed">
        {translate('pages:rules.groups.addHelp')}
      </Text>
    </Stack>
  );
};
